// Settings, in a few clear sections, and every setting by name with a line saying what it does (Features 4, Tidy).
// The index is what Settings' search and the command bar find; a feature that adds a setting adds its line here (or
// with addSettings), and wraps its control in an element with data-setting="<id>" so a search can go to it.

import { set } from "../../state/store";

export type SectionId = "account" | "agents" | "connections" | "notifications" | "keyboard" | "voice" | "appearance" | "data";

export interface Section {
  id: SectionId;
  label: string;
  /** what it holds, in a few words: search words only, not shown in the list */
  blurb: string;
}

export const SECTIONS: Section[] = [
  { id: "account", label: "Account", blurb: "Your name, email and plan" },
  { id: "agents", label: "Agents", blurb: "Models and defaults" },
  { id: "connections", label: "Connections", blurb: "Computers, folders, keys" },
  { id: "notifications", label: "Notifications", blurb: "What pops up, and when" },
  { id: "keyboard", label: "Keyboard", blurb: "Number keys and shortcuts" },
  { id: "voice", label: "Voice", blurb: "Talk to the board" },
  { id: "appearance", label: "Appearance", blurb: "Themes and the toolbar" },
  { id: "data", label: "Trash and backups", blurb: "Deleted things and board files" },
];

export interface SettingDef {
  id: string;
  section: SectionId;
  title: string;
  /** what it does, in one line under it */
  line: string;
  /** other words a person might search for it by */
  words?: string;
  /** on a computer only (a phone has no keyboard shortcuts) */
  desktop?: boolean;
}

const BASE: SettingDef[] = [
  { id: "workspace-name", section: "account", title: "Workspace name", line: "Shown on boards you share, and when an app connects.", words: "name rename title" },
  { id: "email", section: "account", title: "Your email", line: "Sign in with it on any device. We only use it to send sign-in codes.", words: "email sign in login address move account" },
  { id: "plan", section: "account", title: "Plan and subscription", line: "What your plan holds and how much of it you use, and your subscription.", words: "billing payment subscription cancel limits boards streams sources pro price" },
  { id: "x-premium", section: "account", title: "I have X Premium", line: "Posts over 280 characters on X show no warning.", words: "x twitter premium blue 280 characters limit long posts warning" },
  { id: "hands-free", section: "agents", title: "Hands-free", line: "Start new chats, Buttons and jobs without stopping to ask before each tool. Sending, deleting and buying still ask.", words: "hands-free hands free without asking allow approve auto run on its own" },
  { id: "default-models", section: "agents", title: "Default models", line: "The model new chats, jobs, briefs and Buttons start on, one for Claude and one for Codex.", words: "model claude codex opus sonnet haiku fable gpt default thinking" },
  { id: "agent-colours", section: "agents", title: "Show what agents wrote", line: "Agents' words show in their color. Hover to see who wrote what.", words: "colour color agent colours who wrote byline" },
  { id: "computers", section: "connections", title: "Your computers", line: "The computers the chat, jobs and Buttons run on. Link another one, or unlink one.", words: "computer runner server laptop link unlink machine" },
  { id: "servers", section: "connections", title: "Servers on your computers", line: "Which servers on your computers work, and what each needs.", words: "mcp servers connectors plugins sign in failed broken not starting claude code codex status" },
  { id: "folders", section: "connections", title: "Folders agents may use", line: "The folders on your computer agents may read, or read and write.", words: "files folder project read write vault" },
  { id: "keys", section: "connections", title: "Keys", line: "The keys agents and scripts use to reach your boards. Revoke one to cut it off.", words: "api key keys token agent push revoke mcp" },
  { id: "connect", section: "connections", title: "Apps and data", line: "Connect Claude, ChatGPT, Cursor and your data. Opens Connect.", words: "connect mcp claude chatgpt cursor codex stripe data service app" },
  { id: "notify-here", section: "notifications", title: "Show them as notifications", line: "Reminders and what comes due pop up on this computer or phone, with the board open or closed.", words: "notifications push alerts browser bell" },
  { id: "notify-what", section: "notifications", title: "What raises a notification", line: "Reminders always do; choose which of the rest do.", words: "notifications reminders due agents needs you" },
  { id: "quiet-hours", section: "notifications", title: "Quiet hours", line: "Nothing pops up in these hours. It still waits in the bell.", words: "quiet hours do not disturb night sleep" },
  { id: "number-keys", section: "keyboard", title: "Number keys open boards", line: "Press 1 to 9 to open boards by their position in the list.", words: "numbers keys shortcuts boards 1 2 3", desktop: true },
  { id: "shortcuts", section: "keyboard", title: "Every keyboard shortcut", line: "The whole list of keys. Press ? anywhere to see it.", words: "keys hotkeys shortcuts list", desktop: true },
  { id: "voice", section: "voice", title: "Use voice on this computer", line: "Talk to the board. The voice model downloads once and stays in this browser.", words: "voice microphone mic speak talk dictation speech" },
  { id: "look", section: "appearance", title: "Look and themes", line: "Per-board theme and colors.", words: "theme look dark light colour color background picture scene style font" },
  { id: "toolbar", section: "appearance", title: "The toolbar on a canvas", line: "Choose what the bar on the left of a canvas board holds.", words: "rail toolbar tools customize left bar" },
  { id: "trash", section: "data", title: "Trash", line: "Deleted items stay here for 30 days.", words: "trash deleted restore bin recover undo" },
  { id: "board-file", section: "data", title: "Export or import a board", line: "Export a board as a file, without data or keys, or make a board from one.", words: "export import backup download upload file json copy" },
];

const added: SettingDef[] = [];

/** A feature's own settings, added to the index (Hands-free's default, Show tips) so search and the command bar find them. */
export function addSettings(defs: SettingDef[]) {
  for (const d of defs) if (!added.some((a) => a.id === d.id) && !BASE.some((b) => b.id === d.id)) added.push(d);
}

export function allSettings(phone = false): SettingDef[] {
  return [...BASE, ...added].filter((d) => !(phone && d.desktop));
}

export const sectionOf = (id: SectionId) => SECTIONS.find((s) => s.id === id)!;

/** Settings that match what was typed: by title first, then by the words under it, then by the words it is known by. */
export function findSettings(q: string, phone = false): SettingDef[] {
  const t = q.trim().toLowerCase();
  if (!t) return [];
  const words = t.split(/\s+/);
  const scored = allSettings(phone)
    .map((d) => {
      const title = d.title.toLowerCase();
      const hay = `${title} ${d.line.toLowerCase()} ${(d.words ?? "").toLowerCase()} ${sectionOf(d.section).label.toLowerCase()} ${sectionOf(d.section).blurb.toLowerCase()}`;
      const s = title.startsWith(t) ? 4 : title.includes(t) ? 3 : words.every((w) => hay.includes(w)) ? (d.line.toLowerCase().includes(t) ? 2 : 1) : 0;
      return { d, s };
    })
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s);
  return scored.map((x) => x.d);
}

/** Settings' old tabs, as other places still open them, and where each now is. */
const OLD: Record<string, { section: SectionId; setting?: string }> = {
  account: { section: "account" },
  computer: { section: "connections", setting: "computers" },
  agents: { section: "agents" },
  notifications: { section: "notifications" },
  keyboard: { section: "keyboard" },
  voice: { section: "voice" },
  keys: { section: "connections", setting: "keys" },
  trash: { section: "data", setting: "trash" },
  board: { section: "data", setting: "board-file" },
};

/** Where Settings opens for what it was asked: a section, a setting, or one of its old tabs. */
export function settingsTarget(arg: unknown): { section: SectionId; setting?: string } {
  const a = (arg ?? {}) as { tab?: string; section?: string; setting?: string };
  if (a.setting) {
    const d = allSettings().find((x) => x.id === a.setting);
    if (d) return { section: d.section, setting: d.id };
  }
  if (a.section && SECTIONS.some((s) => s.id === a.section)) return { section: a.section as SectionId };
  if (a.tab && OLD[a.tab]) return OLD[a.tab];
  return { section: "account" };
}

/** Opens Settings at a section, or at one setting, which it scrolls to and lights once. */
export function openSettings(at?: { section?: SectionId; setting?: string }) {
  set({ panel: "settings", panelArg: at ?? null });
}
